import { ThemeEvents } from '@theme/events';

/** Quantity cards extend Tinker's native form. They never add a second cart request. */
class BeamProduct extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.section = this.closest('.shopify-section') || this;
    this.initialized = false;
    this.readSnapshot(this);
    this.pending = false;
    this.section.addEventListener(ThemeEvents.variantSelected, () => {
      this.pending = true;
      this.refreshAvailability();
    }, { signal });
    this.section.addEventListener(ThemeEvents.variantUpdate, event => {
      if (String(event.detail?.data?.productId) !== String(this.snapshot?.productId)) return;
      const updated = event.detail.data.html.querySelector(`beam-product[data-section-id="${CSS.escape(this.dataset.sectionId)}"]`);
      if (!updated) return;
      this.readSnapshot(updated);
      // Retain Shopify's server-formatted amounts for all standard offer cards.
      for (const button of this.cards) {
        const source = updated.querySelector(`[data-beam-bundle][data-offer-id="${CSS.escape(button.dataset.offerId)}"] [data-beam-prices]`);
        const target = button.querySelector('[data-beam-prices]');
        if (source && target) target.replaceWith(source.cloneNode(true));
      }
      this.pending = false;
      // Native product-form and quantity listeners finish their synchronous updates first.
      queueMicrotask(() => { this.ensureValidQuantity(); this.refresh(); });
    }, { signal });
    this.section.addEventListener(ThemeEvents.quantitySelectorUpdate, event => {
      if (this.form?.contains(event.target)) this.refresh();
    }, { signal });
    this.addEventListener('click', event => {
      const card = event.target.closest('[data-beam-bundle]');
      if (card && this.contains(card)) this.choose(Number(card.dataset.quantity));
      if (event.target.closest('[data-beam-sticky-add]')) {
        const button = this.addButton;
        if (button && !button.disabled && !this.pending) button.click();
      }
    }, { signal });
    document.addEventListener('click', event => {
      const offer = event.target.closest('[data-beam-choose]');
      if (offer) {
        event.preventDefault();
        if (this.choose(Number(offer.dataset.beamChoose))) {
          this.addButton?.scrollIntoView({ behavior: this.scrollBehavior, block: 'center' });
          this.addButton?.focus({ preventScroll: true });
        }
      }
      const link = event.target.closest('a[href^="#"]');
      if (link && link.closest('[class^="beam-"]')) {
        const id = link.getAttribute('href')?.slice(1);
        const target = id && document.getElementById(id);
        if (target) {
          event.preventDefault();
          if (target.matches('details')) target.open = true;
          target.scrollIntoView({ behavior: this.scrollBehavior, block: 'start' });
        }
      }
    }, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, event => {
      queueMicrotask(() => { this.ensureValidQuantity(); this.refresh(); });
      if (this.dataset.openDrawer !== 'true' || event.detail?.data?.source !== 'product-form-component' || String(event.detail?.data?.productId) !== String(this.snapshot?.productId) || event.detail?.data?.didError) return;
      // Open the theme's drawer when enabled in the theme settings. No substitute cart.
      document.querySelector('cart-drawer-component')?.open?.();
    }, { signal });
    document.addEventListener('shopify:section:load', () => { this.initialize(); this.refresh(); }, { signal });
    document.addEventListener('shopify:block:select', event => {
      const question = event.target.closest?.('details');
      if (question) question.open = true;
    }, { signal });
    window.addEventListener('scroll', () => {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => { this.scrollFrame = 0; this.updateSticky(); });
    }, { passive: true, signal });
    window.addEventListener('resize', () => this.updateSticky(), { passive: true, signal });
    // The theme editor can connect this element before inserting its children.
    // Initialize when the snapshot and native form actually exist, and again if
    // Shopify replaces them. Identity checks prevent our price updates looping.
    this.contentObserver = new MutationObserver(() => this.initialize());
    this.contentObserver.observe(this, { childList: true, subtree: true });
    this.initialize();
    customElements.whenDefined('quantity-selector-component').then(() => {
      if (!this.isConnected || signal.aborted) return;
      this.initialize();
    });
  }
  initialize() {
    const snapshot = this.querySelector('[data-beam-snapshot]');
    const input = this.quantityInput;
    if (!snapshot || !input || !this.cards.length) return;
    if (this.initialized && this.snapshotElement === snapshot && this.initializedInput === input) return;
    if (!this.readSnapshot(this)) return;
    this.snapshotElement = snapshot;
    this.initializedInput = input;
    this.observer?.disconnect();
    if (this.querySelector('[data-beam-sticky]') && this.addButton) {
      this.observer = new IntersectionObserver(() => this.updateSticky(), { threshold: 0 });
      this.observer.observe(this.addButton);
    }
    const preferred = this.initialized ? this.selectedQuantity : Number(this.querySelector('[data-beam-buy-block]')?.dataset.defaultQuantity || 1);
    this.initialized = true;
    if (!this.choose(preferred, false)) this.ensureValidQuantity();
    this.refresh();
  }
  disconnectedCallback() {
    this.abort?.abort();
    this.observer?.disconnect();
    this.contentObserver?.disconnect();
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
  }
  get cards() { return [...this.querySelectorAll('[data-beam-bundle]')]; }
  get form() { return this.querySelector('product-form-component'); }
  get quantityControl() { return this.form?.querySelector('quantity-selector-component'); }
  get quantityInput() { return this.form?.querySelector('input[name="quantity"]'); }
  get quantity() { return Number(this.quantityInput?.value || 1); }
  get addButton() { return this.form?.querySelector('[ref="addToCartButton"]'); }
  get scrollBehavior() { return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'; }
  readSnapshot(source) {
    const content = source.querySelector('[data-beam-snapshot]')?.textContent;
    if (!content?.trim()) return false;
    try { this.snapshot = JSON.parse(content); } catch { return false; }
    return true;
  }
  validQuantity(q) {
    const s = this.snapshot;
    if (!s?.available || this.pending || !Number.isInteger(q) || q < s.min || (q - s.min) % s.step !== 0) return false;
    const inCart = Number(this.quantityInput?.dataset.cartQuantity || 0);
    if (s.max !== null && q + inCart > s.max) return false;
    if (s.inventory !== null && q + inCart > s.inventory) return false;
    return true;
  }
  choose(q, announce = true) {
    if (!this.validQuantity(q) || !this.quantityInput) {
      if (announce) this.announce(this.dataset.quantityError);
      return false;
    }
    this.quantityInput.value = String(q);
    this.selectedQuantity = q;
    // The input is already native; do not call a not-yet-connected custom
    // element's methods while the editor is building the DOM.
    const control = this.quantityControl;
    const ready = control?.refs?.quantityInput === this.quantityInput;
    if (ready) control.updateButtonStates?.();
    this.form.dataset.quantityDefault = String(q);
    this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    if (ready) control.onQuantityChange?.();
    else (control || this.quantityInput).dispatchEvent(new CustomEvent(ThemeEvents.quantitySelectorUpdate, { bubbles: true, detail: { quantity: q } }));
    this.refresh();
    if (announce) this.announce(this.currentCard?.innerText || '');
    return true;
  }
  ensureValidQuantity() {
    if (this.validQuantity(this.quantity)) return;
    const valid = this.cards.find(card => this.validQuantity(Number(card.dataset.quantity)));
    if (valid) this.choose(Number(valid.dataset.quantity), false);
    else if (this.validQuantity(this.snapshot?.min)) this.choose(this.snapshot.min, false);
  }
  get currentCard() { return this.cards.find(card => Number(card.dataset.quantity) === this.quantity); }
  format(amount) {
    return new Intl.NumberFormat(document.documentElement.lang || 'es-MX', { style: 'currency', currency: this.snapshot.currency, currencyDisplay: 'code' }).format(amount / 100);
  }
  discountFor(q) {
    // Native automatic discounts are quantity minima; the highest eligible card percentage wins.
    return this.cards.reduce((best, card) => Number(card.dataset.quantity) <= q ? Math.max(best, Number(card.dataset.discount)) : best, 0);
  }
  amount(q, discount = this.discountFor(q)) {
    const gross = this.snapshot.price * q;
    return gross - Math.round(gross * discount / 100);
  }
  refreshAvailability() {
    for (const card of this.cards) {
      const available = this.validQuantity(Number(card.dataset.quantity));
      card.disabled = !available;
      const note = card.querySelector('[data-beam-unavailable]');
      if (note) note.hidden = available || this.pending;
    }
    const stickyButton = this.querySelector('[data-beam-sticky-add]');
    if (stickyButton) stickyButton.disabled = !this.validQuantity(this.quantity) || !this.addButton || this.addButton.disabled;
  }
  refresh() {
    if (!this.snapshot) return;
    if (this.quantityInput) this.quantityInput.disabled = !this.snapshot.available;
    const q = this.quantity;
    for (const card of this.cards) card.setAttribute('aria-pressed', String(Number(card.dataset.quantity) === q));
    const formatted = this.currentCard?.querySelector('[data-beam-amount]')?.innerHTML || this.format(this.amount(q));
    for (const element of this.querySelectorAll('[data-beam-total]')) {
      element.innerHTML = this.snapshot.available ? formatted : '';
    }
    const subtitle = this.querySelector('[data-beam-sticky-sub]');
    if (subtitle) subtitle.textContent = q === 2 ? this.dataset.stickyDuo : q === 1 ? this.dataset.stickySingle : `${q} ${this.dataset.stickyOther}`;
    for (const title of this.querySelectorAll('[data-beam-title]')) title.textContent = this.snapshot.title;
    for (const card of document.querySelectorAll('[data-beam-offer-card]')) {
      const quantity = Number(card.dataset.quantity);
      const source = this.cards.find(item => Number(item.dataset.quantity) === quantity);
      const button = card.querySelector('[data-beam-choose]');
      if (button) {
        button.disabled = !source || !this.validQuantity(quantity);
        button.setAttribute('aria-pressed', String(quantity === q));
      }
      card.classList.toggle('bvp-is-selected', quantity === q);
      if (!source) continue;
      for (const attr of ['data-beam-amount', 'data-beam-before', 'data-beam-each']) {
        const from = source.querySelector(`[${attr}]`), to = card.querySelector(`[${attr}]`);
        if (from && to) to.innerHTML = from.innerHTML;
      }
      const discount = Number(source.dataset.discount);
      const saving = Math.round(this.snapshot.price * quantity * discount / 100);
      for (const line of card.querySelectorAll('[data-beam-offer-line]')) line.textContent = line.dataset.template.replaceAll('{ahorro}', this.format(saving));
      for (const title of card.querySelectorAll('[data-beam-variant-title]')) title.textContent = this.snapshot.variantTitle ? `${card.querySelector('[data-beam-each]') ? ' · ' : ' '}${this.snapshot.variantTitle}` : '';
    }
    this.refreshAvailability();
    this.updateSticky();
  }
  announce(message) {
    const region = this.querySelector('[data-beam-status]');
    if (region) region.textContent = message || '';
  }
  updateSticky() {
    const sticky = this.querySelector('[data-beam-sticky]');
    if (!sticky) return;
    const rect = this.addButton?.getBoundingClientRect();
    const visible = !!rect && rect.bottom < 0;
    sticky.hidden = !visible;
    sticky.inert = !visible;
    sticky.dataset.visible = String(visible);
  }
}
if (!customElements.get('beam-product')) customElements.define('beam-product', BeamProduct);
